import React from "react";
import { Area } from "../models/Area";
import { Link } from "react-router-dom";
import { computeAreaProgress } from "../utils/areaProgress";
import { colors, fonts, radius, buttonStyles } from "../utils/colors";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { LockClock, ScheduleSend } from "@mui/icons-material";

interface DraftEntry {
  periodName?: string;
  startTime?: string;
  endTime?: string;
  draftAnswers?: any[];
}

interface MainPageProps {
  areas: Area[];
  ratings: Record<string, number>;
  isAreaComplete: (area: Area, ratings: Record<string, number>) => boolean;
  noActivePeriod: boolean;
  turnedIn: boolean;
  isLoggedIn: boolean;
  userName: string | null;
  draftEntry: DraftEntry | null;
  syncState: "idle" | "running" | "done" | "error";
  submitState: "idle" | "submitting" | "success" | "error";
  countdown: string;
  onLogin: () => void;
  onLogout: () => void;
  onSync: () => void;
  onSubmit: () => void;
}

const xenitPic = process.env.PUBLIC_URL + "/Xenit_vm.png";

const formatDate = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric"
      })
    : "—";

const MainPage: React.FC<MainPageProps> = ({
  areas,
  ratings,
  isAreaComplete,
  noActivePeriod,
  turnedIn,
  isLoggedIn,
  userName,
  draftEntry,
  syncState,
  submitState,
  countdown,
  onLogin,
  onLogout,
  onSync,
  onSubmit
}) => {
  const isDevMode = process.env.REACT_APP_DEV_MODE === "true";


  
  const allAreasComplete =
    isLoggedIn &&
    areas.length > 0 &&
    areas.every(a =>
      isAreaComplete ? isAreaComplete(a, ratings) : computeAreaProgress(a, ratings).complete
    );

  return (
    <div
      style={{
        margin: "40px 40px 100px 10px",
        padding: "0 24px",
        maxWidth: "100%",
        fontSize: "1.15rem",
        lineHeight: 1.6,
        fontFamily: fonts.main,
        color: colors.darkBlue,
      }}
    >
      {isDevMode && (
        <div
          style={{
            backgroundColor: "#ff9800",
            color: "white",
            padding: "12px 24px",
            marginBottom: "20px",
            borderRadius: "6px",
            fontWeight: "bold",
            textAlign: "center"
          }}
        >
          🔧 DEV MODE: Using local API at {process.env.REACT_APP_API_BASE_URL} | User ID: {process.env.REACT_APP_DEV_USER_ID}
        </div>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          flexWrap: "nowrap",
          width: "100%"
        }}
      >
        <div
          id="info-section"
          style={{
            flex: "1 1 auto",
            minWidth: "420px",
            maxWidth: "1000px",
            paddingRight: "32px"
          }}
        >
          <h1 style={{ marginBottom: "8px", fontSize: "2.1rem", fontFamily: fonts.main, color: colors.darkBlue }}>
            Data Governance Maturity Model Questionnaire
          </h1>
          <h2
            style={{
              fontWeight: 500,
              fontSize: "1.4rem",
              marginTop: 0,
              fontFamily: fonts.main,
              color: colors.darkBlue,
            }}
          >
            Welcome to Xenit Data Governance Maturity Model Questionnaire
          </h2>
          <p style={{ lineHeight: 1.7, fontSize: "1.05rem", fontFamily: fonts.main, color: colors.mediumGray }}>
            Data is one of your organization’s most valuable assets. <br /> This assessment helps you
            understand how well your organization governs its data today — and where there is room to
            grow.
          </p>

          {turnedIn ? (
            <div
              style={{
                marginTop: 24,
                padding: "16px 20px",
                border: `1px solid ${colors.green}`,
                borderRadius: `${radius.lg}px`,
                background: "rgba(2, 211, 95, 0.06)"
              }}
            >
              <h2 style={{ marginTop: 0, color: colors.green, fontFamily: fonts.main }}>You{"\u2019"}ve turned in {"\u2013"} all set!</h2>
              <p style={{ margin: 0, color: colors.mediumGray }}>Your questionnaire has been submitted. Further edits are disabled.</p>
            </div>
          ) : noActivePeriod ? (
            <div
              style={{
                marginTop: 24,
                padding: "16px 20px",
                border: `1px solid ${colors.pink}`,
                borderRadius: `${radius.lg}px`,
                background: colors.yellow
              }}
            >
              <h2 style={{ marginTop: 0, fontFamily: fonts.main, color: colors.darkBlue }}>No active draft</h2>
              <p style={{ margin: 0, color: colors.mediumGray }}>You don't have a registered active draft right now.</p>
            </div>
          ) : (
            <div>
              <div
                style={{
                  textAlign: "left",
                  margin: "24px 0 8px",
                  display: "block",
                  width: "100%"
                }}
              >
                <b style={{ marginBottom: "6px", fontSize: "1.1rem", color: colors.darkBlue }}>This assessment covers:</b>
                {isLoggedIn && areas.length > 0 ? (
                  <ul style={{ listStyle: "none", padding: 0, marginTop: 8 }}>
                    {areas.map(a => {
                      const prog = computeAreaProgress(a, ratings);
                      const filled = prog.complete;
                      return (
                        <li
                          key={a.areaId}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "34px 1fr 170px",
                            alignItems: "center",
                            columnGap: "10px",
                            padding: "6px 0",
                            fontSize: "1.2rem"
                          }}
                        >
                          <span style={{ textAlign: "center", width: 28 }}>
                            {filled ? (
                              <CheckCircleIcon sx={{ color: colors.green, fontSize: 20 }} />
                            ) : (
                              <RadioButtonUncheckedIcon sx={{ color: colors.lightGray, fontSize: 20 }} />
                            )}
                          </span>
                          <Link
                            to={`/areas/${a.areaId}`}
                            style={{
                              color: colors.darkBlue,
                              textDecoration: "none",
                              display: "inline-flex",
                              alignItems: "center",
                              minHeight: "28px",
                              fontWeight: 500,
                              fontFamily: fonts.main,
                              transition: "color 0.15s ease",
                            }}
                            onMouseEnter={e => (e.currentTarget.style.color = colors.teal)}
                            onMouseLeave={e => (e.currentTarget.style.color = colors.darkBlue)}
                          >
                            {a.areaName}
                          </Link>
                          <span
                            style={{
                              fontSize: ".9rem",
                              color: colors.mediumGray,
                              textAlign: "right",
                              fontVariantNumeric: "tabular-nums",
                              fontFamily: fonts.mono,
                            }}
                          >
                            {prog.ratedCount}/{prog.totalCount} ({prog.percent}%)
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <ul style={{ listStyleType: "none", paddingLeft: 0, marginTop: 8 }}>
                    {[
                      "Data Governance Strategy",
                      "Data Governance Organization",
                      "Data Quality Management",
                      "Metadata Management",
                      "Master Data Management",
                      "Data Security & Access",
                      "Data Literacy Program"
                    ].map((item, i) => (
                      <li
                        key={i}
                        style={{
                          marginBottom: "10px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontSize: "1.05rem"
                        }}
                      >
                        <span style={{ fontSize: "1.1rem" }}>✅</span> <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div style={{ marginTop: "20px" }}>
            <Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end", alignItems: "center" }}>
              <Button
                variant="outlined"
                size="large"
                onClick={onSync}
                disabled={!isLoggedIn || syncState === "running" || turnedIn || noActivePeriod}
                sx={{
                  ...buttonStyles.secondary,
                  fontSize: "1rem",
                  padding: "10px 20px",
                }}
              >
                {turnedIn
                  ? "Sync disabled"
                  : syncState === "running"
                  ? "Syncing..."
                  : syncState === "done"
                  ? "Sync complete!"
                  : syncState === "error"
                  ? "Sync failed, retry"
                  : "Sync all answers"}
              </Button>
              <Button
                variant="contained"
                size="large"
                disabled={
                  turnedIn || noActivePeriod || !allAreasComplete || submitState === "submitting" || !isLoggedIn
                }
                onClick={onSubmit}
                sx={{
                  ...buttonStyles.primary,
                  fontSize: "1rem",
                  padding: "10px 24px",
                  backgroundColor:
                    submitState === "success"
                      ? colors.green
                      : allAreasComplete
                      ? colors.darkBlue
                      : colors.mediumGray,
                  "&:hover": {
                    backgroundColor:
                      submitState === "success"
                        ? colors.teal
                        : allAreasComplete
                        ? colors.darkGreen
                        : colors.mediumGray,
                    transform: "scale(1.02)",
                  }
                }}
              >
                {turnedIn && "Already submitted"}
                {!turnedIn && submitState === "submitting" && "Submitting..."}
                {!turnedIn && submitState === "success" && "Submitted!"}
                {!turnedIn && submitState === "error" && "Submit failed"}
                {!turnedIn &&
                  submitState === "idle" &&
                  !allAreasComplete &&
                  "Complete all areas"}
                {!turnedIn &&
                  submitState === "idle" &&
                  allAreasComplete &&
                  "Submit"}
              </Button>
            </Stack>
          </div>
        </div>

        {/* Login / status section */}
        <div
          id="login-section"
          style={{
            flex: "0 1 440px",
            minWidth: "340px",
            marginLeft: "auto",
            borderLeft: `1px solid ${colors.lightGray}`,
            paddingLeft: "24px",
            paddingTop: "8px",
            position: "sticky",
            top: "40px"
          }}
        >
          {isLoggedIn && userName ? (
            <div>
              <Stack
                sx={{ justifyContent: "space-between", alignItems: "center", gap: 2 }}
                direction="row"
              >
                <p style={{ fontSize: "1rem", margin: 0, wordBreak: "break-word", fontFamily: fonts.main }}>
                  Welcome, <strong>{userName}</strong>!
                </p>
                <Button
                  onClick={onLogout}
                  variant="contained"
                  size="small"
                  sx={{
                    ...buttonStyles.secondary,
                    fontSize: "0.9rem",
                    padding: "5px 13px",
                  }}
                >
                  Sign out
                </Button>
              </Stack>

              {draftEntry && (
                <div
                  style={{
                    marginTop: "18px",
                    padding: "12px 14px",
                    border: `1px solid ${colors.lightGray}`,
                    borderRadius: `${radius.lg}px`,
                    background: colors.offWhite,
                    fontSize: ".9rem",
                    lineHeight: 1.4,
                    fontFamily: fonts.main,
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 6, color: colors.darkBlue }}>Current Draft Period</div>
                  <div>
                    <strong>Name:</strong> {draftEntry.periodName || "—"}
                  </div>
                  <div>
                    <strong>Start:</strong> {formatDate(draftEntry.startTime)}
                  </div>
                  <div>
                    <strong>End:</strong> {formatDate(draftEntry.endTime)}
                  </div>
                  <div style={{ marginTop: 4, color: colors.mediumGray }}>
                    Answers loaded: {(draftEntry.draftAnswers ?? []).length}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <h1 style={{ marginBottom: "8px", fontFamily: fonts.main, color: colors.darkBlue }}>Sign in</h1>
              <p style={{ lineHeight: 1.4, color: colors.mediumGray, fontFamily: fonts.main }}>
                You have been invited to use this service via your work email.
                <br />
                Please sign in using Microsoft Entra ID.
              </p>
              <div style={{ margin: "24px 0" }}>
                <Button
                  onClick={onLogin}
                  variant="contained"
                  sx={{
                    ...buttonStyles.primary,
                    fontSize: "1rem",
                    padding: "10px 24px",
                  }}
                >
                  Sign in
                </Button>
              </div>
            </div>
          )}

          <div
            className="xenit-image-container"
            style={{
              position: "fixed",
              bottom: "50px",
              right: "50px",
              zIndex: 50,
              textAlign: "end"
            }}
          >
            <img
              src={xenitPic}
              alt="Xenit"
              style={{ height: "460px", width: "460px", objectFit: "contain", opacity: 0.9 }}
            />
            {countdown && (
              <h2
                style={{
                  marginTop: "20px",
                  fontSize: "1.8rem",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: "8px",
                  color: colors.darkBlue,
                  fontFamily: fonts.main,
                }}
              >
                <ScheduleSend fontSize="inherit" />
                <small>{countdown}</small>
              </h2>
            )}
            {noActivePeriod && (
              <h2
                style={{
                  marginTop: "20px",
                  fontSize: "1.8rem",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: "8px",
                  color: colors.pink,
                  fontFamily: fonts.main,
                }}
              >
                <LockClock fontSize="inherit" />
                <small>No active period</small>
              </h2>
            )}                 
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainPage;
